import { useMemo, useState } from 'react';
import { mediaUrl, trpc } from '@tg/app-core';
import { typeOfTitle, blockName, blockTypeLabel, clientLine, cloneContent, dayHas, factDetail, isTextBlock, textLines, type Block, type ComplexFormat, type Line, type WorkoutContent } from '@tg/domain';
import { Link } from 'react-router-dom';
import { ICON } from './icons';
import { Svg } from './Svg';
import { useCtx, plural } from './ctx';
import { MONTHS_N, RU, dowMon, monthEnd, monthStart, shiftMonth, shortDate, today } from './dates';
import type { Who } from './Constructor';
import { MRailHead } from '../shell/Mobile';
import { Cav } from '../ui/Cav';

const GROUP_ORDER = ['Ноги', 'Спина', 'Грудь', 'Плечи', 'Руки', 'Кор', 'Кардио', 'Шея', 'ТА', 'Кроссфит', 'Мобилити'];

/* Боковая панель материала: «Календарь» любого клиента или группы и «Базы шаблонов» */
export function Rail(props: {
  who: Who;
  whoLabel: { ini: string; name: string; photo?: string | null };
  date: string;
  onPickWho: (cb: (w: Who) => void) => void;
  onCopyDay: (c: WorkoutContent) => void;
  onAddBlock: (b: Block) => void;
  onAddLine: (l: Line) => void;
  onAddExercise: (exId: string) => void;
  /** Телефон: закрыть шторку панели */
  onCloseSheet: () => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [src, setSrc] = useState<'cal' | 'tpl'>('cal');
  return (
    <aside className={'rail' + (collapsed ? ' min' : '')} data-min="Календарь · Базы шаблонов">
      <MRailHead title="Календарь и базы" onClose={props.onCloseSheet} />
      <button className="railtog" title={collapsed ? 'Развернуть панель' : 'Свернуть панель'} aria-label="Свернуть панель" onClick={() => setCollapsed(!collapsed)}>
      </button>
      {!collapsed && (src === 'cal' ? <CalSource {...props} src={src} setSrc={setSrc} /> : <TplSource {...props} src={src} setSrc={setSrc} />)}
    </aside>
  );
}

function SrcSw({ src, setSrc }: { src: string; setSrc: (s: 'cal' | 'tpl') => void }) {
  return (
    <div className="srcsw">
      <button data-srcsw="cal" className={src === 'cal' ? 'on' : ''} onClick={() => setSrc('cal')}>
        <Svg src={ICON.cal} />
        <span>Календарь</span>
      </button>
      <button data-srcsw="tpl" className={src === 'tpl' ? 'on' : ''} onClick={() => setSrc('tpl')}>
        <Svg src={ICON.folder} />
        <span>Базы шаблонов</span>
      </button>
    </div>
  );
}

const lineChip = (y: Line, ex: (id: string) => any) => {
  if (y.t === 'chain') return clientLine(y, null, ex).split(': ').slice(1).join(': ');
  if (y.txt) return y.txt;
  const l = y.load;
  const load = !l ? '' : (l.from + (l.to != null ? '–' + l.to : '') + ' ' + (l.kind === 'pct' ? '%' : l.unit)).replace('.', ',');
  return [y.scheme, load].filter(Boolean).join(' · ');
};

function CalSource(p: Parameters<typeof Rail>[0] & { src: string; setSrc: (s: 'cal' | 'tpl') => void }) {
  const c = useCtx();
  const [who, setWho] = useState<Who | null>(null);
  const [label, setLabel] = useState<{ ini: string; name: string; photo?: string | null } | null>(null);
  const w = who ?? p.who;
  const [month, setMonth] = useState(p.date.slice(0, 7));
  const days = trpc.trainer.days.useQuery({ who: w, from: monthStart(month + '-01'), to: monthEnd(month + '-01') });
  const last = trpc.trainer.lastBefore.useQuery({ who: w, date: p.date });
  const [sel, setSel] = useState<string | null>(null);
  const map = new Map((days.data || []).filter((d) => dayHas(d.content)).map((d) => [d.date, d]));
  const cur = sel ?? last.data ?? null;
  const day = cur ? map.get(cur) ?? null : null;
  const lead = dowMon(month + '-01');
  const n = +monthEnd(month + '-01').slice(8);
  const [y, m] = month.split('-').map(Number);
  const t = today();
  const content = day?.content;
  /* У клиента под блоками — его итог и переписка к этой тренировке (только чтение): пишешь новую
     и видишь, как прошла прошлая и что он о ней сказал (прототип: blkExtraHTML) */
  const cid = 'clientId' in w ? w.clientId : '';
  const resQ = trpc.trainer.results.useQuery({ clientId: cid, from: cur || p.date, to: cur || p.date }, { enabled: !!cid && !!cur });
  const talkQ = trpc.trainer.thread.useQuery({ clientId: cid, date: cur || p.date }, { enabled: !!cid && !!cur });
  const doneDay = !!resQ.data?.completions.some((x) => x.blockId === '');

  return (
    <>
      <div className="railhead">
        <SrcSw src={p.src} setSrc={p.setSrc} />
        <button
          className="csrc-cli"
          title="Чей календарь смотреть"
          onClick={() =>
            p.onPickWho((nw) => {
              setWho(nw);
              setSel(null);
              setLabel(null);
            })
          }
        >
          <Cav ini={(label ?? p.whoLabel).ini} photo={(label ?? p.whoLabel).photo} />
          <span className="cl-t">
            <b>{(label ?? p.whoLabel).name}</b>
            <s>{who ? 'календарь для копирования' : 'клиент в конструкторе'}</s>
          </span>
          <Svg src={ICON.chev} />
        </button>
        <div className="mcal">
          <div className="mcal-h">
            <button data-mcal="-1" title="Предыдущий месяц" onClick={() => setMonth(shiftMonth(month, -1))}>
              ‹
            </button>
            <b>
              {MONTHS_N[m - 1]} {y}
            </b>
            <button data-mcal="1" title="Следующий месяц" onClick={() => setMonth(shiftMonth(month, 1))}>
              ›
            </button>
          </div>
          <div className="mcal-g">
            {RU.map((d) => (
              <s key={d}>{d}</s>
            ))}
            {Array.from({ length: lead }, (_, k) => (
              <span key={'p' + k} className="pad"></span>
            ))}
            {Array.from({ length: n }, (_, k) => {
              const ds = month + '-' + String(k + 1).padStart(2, '0');
              const x = map.get(ds);
              const cls = [x ? 'has ' + (x.status === 'published' ? 'pub' : 'draft') : '', ds === cur ? 'on' : '', ds === t ? 'today' : '', ds === p.date && !who ? 'cur' : '']
                .filter(Boolean)
                .join(' ');
              return (
                <button key={ds} className={cls} data-mday={ds} disabled={!x} onClick={() => setSel(ds)}>
                  {k + 1}
                  {x && <i></i>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="railbody">
        {content ? (
          <div className="csrc">
            <div className="csrc-h">
              <b>{content.title || 'Без названия'}</b>
              <s>
                {shortDate(day!.date)} · {content.blocks.length} {plural(content.blocks.length, 'блок', 'блока', 'блоков')}
                {cid && doneDay ? ' · выполнена' : ''}
              </s>
              {cid ? (
                <Link className="csrc-open" to={`/workout?client=${cid}&date=${day!.date}`}>
                  Открыть тренировку и переписку
                </Link>
              ) : null}
            </div>
            <button className="btn sm csrc-copy" onClick={() => p.onCopyDay(cloneContent(content))}>
              <Svg src={ICON.copy} /> Скопировать в {shortDate(p.date)}
            </button>
            {content.blocks.map((b, bi) => (
              <div className="csb" key={b.id} draggable data-cblk={bi}>
                <div className="csb-h">
                  <span className="gr">
                    <Svg src={ICON.grip} />
                  </span>
                  <span className="tt">
                    <b>{blockName(b) || 'Блок ' + (bi + 1)}</b>
                    {b.kind && b.title && (typeOfTitle(b.title) !== b.kind || (b.kind === 'complex' && b.fmt)) && <s className="ty">{blockTypeLabel(b)}</s>}
                  </span>
                  <button className="add" title="Добавить блок в тренировку" onClick={() => p.onAddBlock(cloneContent(b))}>
                    <Svg src={ICON.plus} />
                  </button>
                </div>
                {isTextBlock(b)
                  ? textLines(b.text).map((l, k) => (
                      <div className="csi raw" key={k}>
                        <span className="nm">{l}</span>
                      </div>
                    ))
                  : b.items
                      .flatMap((y) => (y.t === 'set' ? y.items : [y]))
                      .map((y) => (
                        <div className="csi " draggable key={y.id}>
                          <span className="gr">
                            <Svg src={ICON.grip} />
                          </span>
                          <span className="nm">{y.t === 'chain' ? clientLine(y, null, c.catalog.ex).split(':')[0] : y.exId ? c.catalog.ex(y.exId)?.ru : y.raw}</span>
                          <em>{lineChip(y, c.catalog.ex)}</em>
                          <button className="add" title="Добавить в тренировку" onClick={() => p.onAddLine(cloneContent(y))}>
                            <Svg src={ICON.plus} />
                          </button>
                        </div>
                      ))}
                {cid ? <BlockExtra name={(label ?? p.whoLabel).name.split(' ')[0]} b={b} results={(resQ.data?.results || []).filter((r) => r.blockId === b.id)} talk={(talkQ.data || []).filter((m) => blockOwns(b, m.refId))} /> : null}
              </div>
            ))}
          </div>
        ) : (
          !days.isLoading && <div className="csrc-empty">Выберите день с тренировкой</div>
        )}
      </div>
    </>
  );
}

function TplSource(p: Parameters<typeof Rail>[0] & { src: string; setSrc: (s: 'cal' | 'tpl') => void }) {
  const c = useCtx();
  const [tab, setTab] = useState<'ex' | 'blk'>('ex');
  const [q, setQ] = useState('');
  const blocks = trpc.trainer.templates.useQuery({ level: 'block' }, { enabled: tab === 'blk' });
  const groups = useMemo(() => {
    const list = q.trim() ? c.catalog.matcher.suggest(q, 200) : c.catalog.list;
    const by = new Map<string, typeof list>();
    for (const e of list) (by.get(e.group) || by.set(e.group, []).get(e.group)!).push(e);
    return [...by.entries()].sort((a, b) => {
      const ia = GROUP_ORDER.indexOf(a[0]);
      const ib = GROUP_ORDER.indexOf(b[0]);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  }, [q, c.catalog]);
  return (
    <>
      <div className="railhead">
        <SrcSw src={p.src} setSrc={p.setSrc} />
        <div className="tabs">
          <button data-tab="ex" className={tab === 'ex' ? 'on' : ''} onClick={() => setTab('ex')}>
            Упражнения
          </button>
          <button data-tab="blk" className={tab === 'blk' ? 'on' : ''} onClick={() => setTab('blk')}>
            Блоки
          </button>
        </div>
        <label className="search">
          <Svg src={ICON.search} />
          <input placeholder="Поиск…" autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>
      <div className="railbody">
        {tab === 'ex'
          ? groups.map(([g, list]) => (
              <div key={g} style={{ display: 'contents' }}>
                <div className="grpttl">
                  <span className="lab">{g}</span>
                  <span className="ln"></span>
                  <span className="n">{list.length}</span>
                </div>
                {list.slice(0, q ? 200 : 40).map((e) => (
                  <div className="exc" draggable data-ex={e.id} key={e.id} onClick={() => p.onAddExercise(e.id)}>
                    <span className={'thumb ' + (e.img ? '' : 'none')}>{e.img && <img src={mediaUrl(e.img)} alt="" loading="lazy" />}</span>
                    <span className="body">
                      <span className="nm">{e.ru}</span>
                      <span className="en">
                        {e.en} · {e.equipment}
                      </span>
                    </span>
                    {/* Визуальная подсказка при наведении — не кликабельна (.exc .add{pointer-events:none} в прототипе): клик обрабатывает вся строка, как и там */}
                    <button className="add" data-addex={e.id} title="В открытый блок" tabIndex={-1}>
                      <Svg src={ICON.plus} />
                    </button>
                  </div>
                ))}
              </div>
            ))
          : (blocks.data || [])
              .filter((t) => !q || t.title.toLowerCase().includes(q.toLowerCase()))
              .map((t) => {
                const b = t.content as Block;
                return (
                  <div className="csb" key={t.id}>
                    <div className="csb-h">
                      <span className="gr">
                        <Svg src={ICON.grip} />
                      </span>
                      <span className="tt">
                        <b>{t.title}</b>
                        <s className="ty">{[t.folder, t.trainerId ? 'своё' : 'общее'].filter(Boolean).join(' · ')}</s>
                      </span>
                      <button className="add" title="Добавить блок в тренировку" onClick={() => p.onAddBlock(cloneContent({ ...b, title: b.title || t.title }))}>
                        <Svg src={ICON.plus} />
                      </button>
                    </div>
                    {!isTextBlock(b) &&
                      b.items
                        .flatMap((y) => (y.t === 'set' ? y.items : [y]))
                        .map((y) => (
                          <div className="csi " key={y.id}>
                            <span className="nm">{y.t === 'chain' ? clientLine(y, null, c.catalog.ex).split(':')[0] : y.exId ? c.catalog.ex(y.exId)?.ru : y.raw}</span>
                            <em>{lineChip(y, c.catalog.ex)}</em>
                          </div>
                        ))}
                  </div>
                );
              })}
      </div>
    </>
  );
}

/** Сообщение относится к блоку: к нему самому или к строке внутри */
const blockOwns = (b: Block, ref: string | null) => !!ref && (ref === b.id || (b.items || []).some((y) => y.id === ref || (y.t === 'set' && y.items.some((l) => l.id === ref))));
const hhmm = (t: Date | string) => {
  const x = new Date(t);
  return String(x.getDate()).padStart(2, '0') + '.' + String(x.getMonth() + 1).padStart(2, '0') + ', ' + String(x.getHours()).padStart(2, '0') + ':' + String(x.getMinutes()).padStart(2, '0');
};

/* Итог и чат клиента под блоком прошлой тренировки (правая панель): только чтение; ждёт ответа — открыто */
function BlockExtra({ name, b, results, talk }: { name: string; b: Block; results: { lineId: string; exId: string | null; plan: unknown; fact: unknown }[]; talk: { id: string; author: string; text: string; createdAt: Date | string }[] }) {
  const c = useCtx();
  const order: string[] = [];
  for (const y of b.items || []) for (const l of y.t === 'set' ? y.items : [y]) order.push(l.id);
  const res = results
    .slice()
    .sort((a, z) => order.indexOf(a.lineId) - order.indexOf(z.lineId))
    .map((r) => {
      const plan = (r.plan || {}) as { sets?: number; text?: string; fmt?: ComplexFormat };
      const v = r.lineId ? factDetail(plan, r.fact as Record<string, unknown>) : factDetail(null, r.fact as Record<string, unknown>, plan.fmt ?? b.fmt ?? null);
      /* «3×5 Становая тяга» — схема и упражнение, вес — в значении */
      const label = r.lineId ? (plan.text || '').replace(/\s*[\d,.]+(?:\s*[–-]\s*[\d,.]+)?\s*кг\s*$/, '') || (r.exId && c.catalog.ex(r.exId)?.ru) || 'Упражнение' : b.fmt ? 'Комплекс' : 'Итог';
      return { k: r.lineId || 'f', label, v };
    })
    .filter((x) => x.v);
  const wait = talk.length > 0 && talk[talk.length - 1].author === 'client';
  const st = wait ? <i className="wst q">ждёт ответа</i> : talk.some((m) => m.author === 'client') ? <i className="wst">отвечено</i> : null;
  return (
    <>
      {res.length > 0 && (
        <div className="wres">
          <b>Итог</b>
          {res.map((x) => (
            <span key={x.k}>
              <s>{x.label}</s> <em>{x.v}</em>
            </span>
          ))}
        </div>
      )}
      {talk.length > 0 && (
        <details className={'wtd' + (wait ? ' wait' : '')} open={wait}>
          <summary>
            <Svg src={ICON.chat} />
            <b>Чат · {talk.length}</b>
            {st}
          </summary>
          <div className="wtl">
            {talk.map((m, j) => (
              <div key={m.id} className={'wtm' + (wait && j === talk.length - 1 ? ' q' : '')}>
                <s>
                  {hhmm(m.createdAt)} · {m.author === 'trainer' ? 'Вы' : name}:
                </s>{' '}
                {m.text}
              </div>
            ))}
          </div>
        </details>
      )}
    </>
  );
}
